import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFileInvoice,
  faLocationDot,
  faPenToSquare,
  faPlus,
  faTruckFast,
  faUpRightFromSquare,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import type { Customer, CustomerContact, CustomerLocation } from '@/services/customer.service';
import type { AddressSelection } from '@/services/equipment-intake.service';
import { resolveAddressPreview, type AddressPreview } from './address-preview';

/**
 * The four addresses of an intake, all of them visible, all of the time.
 *
 * This replaces a compact/preview experiment that hid the addresses behind an
 * eye toggle. It failed for a reason worth recording: an operator confirming a
 * shipping address cannot confirm it from a place name, and an icon alone does
 * not distinguish "where it goes back" from "who pays". Height is not the
 * scarce resource here — certainty is.
 *
 * Two sections, two columns each, so the pairs that get confused sit side by
 * side and can be compared rather than remembered:
 *
 *   SERVICE INFORMATION   Received From  |  Contact Person
 *   DELIVERY & BILLING    Return Address |  Invoice Address
 *
 * Purely presentational. Every mutation is delegated upward, so none of the
 * frozen wiring can be affected from this file.
 */

function previewLines(p: AddressPreview | null): string[] {
  if (!p) return [];
  const region = [p.city, p.state, p.postalCode].filter(Boolean).join(', ');
  return [p.line1, p.line2, region, p.country, p.phone].filter((v): v is string => !!v);
}

export function AddressOverview({
  customer,
  locations,
  contacts,
  locationId,
  contactId,
  billTo,
  shipTo,
  onPickLocation,
  onAddLocation,
  onEditLocation,
  onPickContact,
  onAddContact,
  onEditContact,
  onPickReturn,
  onPickInvoice,
  disabled = false,
}: {
  customer: Customer | null;
  locations: CustomerLocation[];
  contacts: CustomerContact[];
  locationId: string;
  contactId: string;
  billTo: AddressSelection;
  shipTo: AddressSelection;
  onPickLocation: () => void;
  onAddLocation: () => void;
  onEditLocation: () => void;
  onPickContact: () => void;
  onAddContact: () => void;
  onEditContact: () => void;
  onPickReturn: () => void;
  onPickInvoice: () => void;
  disabled?: boolean;
}) {
  const location = locations.find((l) => l.id === locationId) ?? null;
  const contact = contacts.find((c) => c.id === contactId) ?? null;

  const returnPreview = useMemo(
    () => resolveAddressPreview('Ship-To', shipTo, customer, locations),
    [shipTo, customer, locations],
  );
  const invoicePreview = useMemo(
    () => resolveAddressPreview('Bill-To', billTo, customer, locations),
    [billTo, customer, locations],
  );

  return (
    <div className="space-y-5">
      <Section title="Service Information">
        <Block
          icon={faLocationDot}
          label="Received From"
          title={location?.name ?? null}
          empty="No location chosen"
          lines={
            location
              ? [
                  location.addressLine1 ?? location.address,
                  location.addressLine2,
                  [location.city, location.state, location.postalCode].filter(Boolean).join(', '),
                  location.phone,
                ].filter((v): v is string => !!v)
              : []
          }
          tooltip="Click to change location"
          onChange={onPickLocation}
          onAdd={onAddLocation}
          onEdit={location ? onEditLocation : undefined}
          disabled={disabled}
        />
        <Block
          icon={faUser}
          label="Contact Person"
          title={contact ? `${contact.firstName} ${contact.lastName}`.trim() : null}
          empty={locationId ? 'No contact chosen' : 'Choose where it came from first'}
          lines={
            contact
              ? [contact.phone, contact.email, contact.designation].filter((v): v is string => !!v)
              : []
          }
          tooltip="Click to change contact person"
          onChange={locationId ? onPickContact : undefined}
          onAdd={locationId ? onAddContact : undefined}
          onEdit={contact ? onEditContact : undefined}
          disabled={disabled}
        />
      </Section>

      <Section title="Delivery &amp; Billing">
        <Block
          icon={faTruckFast}
          label="Shipping Address"
          title={returnPreview?.label ?? null}
          empty="Not set"
          lines={previewLines(returnPreview)}
          tooltip="Click to change shipping address"
          onChange={onPickReturn}
          onEdit={onPickReturn}
          disabled={disabled}
        />
        <Block
          icon={faFileInvoice}
          label="Billing Address"
          title={invoicePreview?.label ?? null}
          empty="Not set"
          lines={previewLines(invoicePreview)}
          tooltip="Click to change billing address"
          // The tax identity belongs to the invoice and to nothing else. The
          // return block above is not even given the option.
          gst={invoicePreview && 'gstNumber' in invoicePreview ? invoicePreview.gstNumber : null}
          onChange={onPickInvoice}
          onEdit={onPickInvoice}
          disabled={disabled}
        />
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {/* Permanent two columns on desktop; stacked in source order on mobile. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Block({
  icon,
  label,
  title,
  empty,
  lines,
  gst,
  tooltip,
  onChange,
  onAdd,
  onEdit,
  disabled,
}: {
  icon: IconDefinition;
  label: string;
  title: string | null;
  empty: string;
  lines: string[];
  gst?: string | null;
  /** Shown on hover. Only rendered when the title actually does something. */
  tooltip?: string;
  onChange?: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  disabled?: boolean;
}) {
  const clickable = !!onChange && !disabled;

  // Negative margin so the hover fill reads as a target around the title rather
  // than a stripe butted against the card edge.
  const titleClass = [
    '-mx-1.5 -my-0.5 inline-flex max-w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-left',
    'transition-colors duration-200 no-underline',
    clickable ? 'cursor-pointer hover:bg-muted hover:text-primary' : 'cursor-default',
  ].join(' ');

  return (
    <div className="flex flex-col rounded-lg border border-border bg-background p-3">
      {/* The icon is decorative — the words carry the meaning. */}
      <div className="mb-1.5 flex items-center gap-2">
        <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>

      {title ? (
        <>
          <button
            type="button"
            onClick={onChange}
            disabled={!clickable}
            title={clickable ? tooltip : undefined}
            className={`${titleClass} group text-sm font-bold text-foreground`}
          >
            <span className="truncate">{title}</span>
            {clickable && (
              // The affordance. Faded until hover so it hints rather than shouts
              // on a screen showing four of them at once.
              <FontAwesomeIcon
                icon={faUpRightFromSquare}
                className="h-3 w-3 shrink-0 opacity-40 transition-opacity duration-200 group-hover:opacity-100"
              />
            )}
          </button>
          <div className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
            {lines.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
            {gst && <div className="mt-1 font-medium text-foreground">GSTIN: {gst}</div>}
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={onChange}
          disabled={!clickable}
          title={clickable ? tooltip : undefined}
          className={`${titleClass} group text-sm italic text-muted-foreground`}
        >
          <span className="truncate">{empty}</span>
          {clickable && (
            <FontAwesomeIcon
              icon={faUpRightFromSquare}
              className="h-3 w-3 shrink-0 opacity-40 transition-opacity duration-200 group-hover:opacity-100"
            />
          )}
        </button>
      )}

      <div className="mt-auto flex flex-wrap gap-2 pt-3">
        {onAdd && <ActionButton icon={faPlus} text="Add" onClick={onAdd} disabled={disabled} />}
        <ActionButton
          icon={faPenToSquare}
          text="Edit"
          onClick={onEdit}
          disabled={disabled || !onEdit}
        />
      </div>
    </div>
  );
}

function ActionButton({
  icon,
  text,
  onClick,
  disabled,
}: {
  icon: IconDefinition;
  text: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
    >
      <FontAwesomeIcon icon={icon} className="h-3 w-3" />
      {text}
    </button>
  );
}
